import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  Snackbar,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../../config/api';

interface ConsultationFormProps {
  title: string;
  formName: string;
  formSource: string;
  bulletPoints?: string[];
  additionalFields?: {
    name: string;
    label: string;
    type: 'text' | 'email' | 'tel' | 'select' | 'textarea';
    required?: boolean;
    options?: { value: string; label: string }[];
    rows?: number;
  }[];
}

export default function ConsultationForm({
  title,
  formName,
  formSource,
  bulletPoints = [],
  additionalFields = []
}: ConsultationFormProps) {
  const [formData, setFormData] = useState<Record<string, string>>({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    ...additionalFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {})
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const params = {
        form_name: formName,
        form_source: formSource,
        form_data: JSON.stringify(formData)
      };

      const response = await api.get('/api/forms/submit', { params });
      const { data } = response;

      if (data.success) {
        setSnackbar({
          open: true,
          message: data.message || 'Form submitted successfully!',
          severity: 'success'
        });
        // Clear form
        setFormData(prev => 
          Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: '' }), {})
        );
      } else {
        throw new Error(data.message || 'Form submission failed');
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || error.message || 'An error occurred. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ bgcolor: '#f7fafd', py: 8 }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        {/* Left Column - Knowledge Points */}
        <Box sx={{ flex: 1, pr: { md: 4 } }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {title}
          </Typography>
          {bulletPoints.length > 0 && (
            <>
              <Typography variant="subtitle1" color="text.secondary" paragraph>
                Our team of experts is ready to help you:
              </Typography>
              <Stack spacing={2}>
                {bulletPoints.map((point, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <CheckCircleIcon color="primary" sx={{ mt: 0.5 }} />
                    <Typography variant="body1">{point}</Typography>
                  </Box>
                ))}
              </Stack>
            </>
          )}
        </Box>
        {/* Right Column - Form */}
        <Box sx={{ flex: 1 }}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 4, bgcolor: 'white', height: '100%' }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Request a Free Consultation
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                fullWidth
              />
              <TextField
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                fullWidth
              />
              <TextField
                label="Company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                fullWidth
              />
              <TextField
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
              />
              {additionalFields.map((field) => {
                if (field.type === 'select') {
                  return (
                    <TextField
                      key={field.name}
                      select
                      label={field.label}
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleInputChange}
                      required={field.required}
                      fullWidth
                    >
                      {field.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  );
                }
                return (
                  <TextField
                    key={field.name}
                    label={field.label}
                    name={field.name}
                    type={field.type}
                    value={formData[field.name]}
                    onChange={handleInputChange}
                    required={field.required}
                    fullWidth
                    multiline={field.type === 'textarea'}
                    rows={field.type === 'textarea' ? field.rows : undefined}
                  />
                );
              })}
              <TextField
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ fontWeight: 600, mt: 2 }}
              >
                {loading ? 'Submitting...' : 'Schedule a Consultation'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
} 